import type { WidgetConfig } from "@workspace/contracts/embed"

import { partClass } from "../appearance"
import { X } from "../icons"
import type { SurfaceProps } from "../surface"

/**
 * The banner: a strip along the top or the bottom of the page in the accent,
 * its line and its CTA - the preview's styles number for number, plus the
 * close the preview has no need of (the `close` part), gone when pinned.
 */

type BannerConfig = Extract<WidgetConfig, { type: "banner" }>

export function BannerSurface({
  payload,
  look,
  reporter,
  open,
  setOpen,
  pinned,
}: SurfaceProps) {
  const config = payload.config as BannerConfig
  const { accentFill, accentText, rad } = look
  if (!open) return null

  // The preview shows the pill whenever there IS a CTA - a label with no
  // link yet is still the customer's label. Without an href it goes nowhere
  // (`#`, same page), like a popup's CTA block; with one, a new tab.
  const cta = config.cta?.label.trim() ? config.cta : null
  const href = cta?.href.trim() || ""
  const edge = config.position === "bottom" ? { bottom: 0 } : { top: 0 }

  return (
    <div
      class={partClass(config, "strip", "uq-fixed uq-fade")}
      role="region"
      aria-label="Announcement"
      style={{
        left: 0,
        right: 0,
        ...edge,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        padding: "11px 14px",
        background: accentFill,
        color: accentText,
      }}
    >
      <span
        class={partClass(config, "text")}
        style={{ fontSize: 12.5, fontWeight: 500 }}
      >
        {config.text}
      </span>
      {cta && (
        <a
          class={partClass(config, "cta")}
          href={href || "#"}
          target={href ? "_blank" : undefined}
          rel={href ? "noopener noreferrer" : undefined}
          onClick={() =>
            reporter.report("cta.clicked", {
              widgetId: payload.id,
              payload: { href },
            })
          }
          style={{
            fontSize: 11.5,
            padding: "5px 10px",
            borderRadius: rad,
            border: `1px solid ${accentText}`,
            whiteSpace: "nowrap",
          }}
        >
          {cta.label}
        </a>
      )}
      {!pinned && (
        <button
          type="button"
          class={partClass(config, "close")}
          onClick={() => setOpen(false)}
          aria-label="Close"
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            width: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.7,
          }}
        >
          <X size={14} />
        </button>
      )}
    </div>
  )
}
